import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import api from '@/lib/api';
import { AuthContext } from '@/contexts/AuthContext';
import '@/App.css';

import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import Dashboard from '@/pages/Dashboard';
import GamblingBudgetCalculator from '@/pages/GamblingBudgetCalculator';
import Cost2PlayCalculator from '@/pages/Cost2PlayCalculator';
import RTPComparisonEngine from '@/pages/RTPComparisonEngine';
import CasinoPartners from '@/pages/CasinoPartners';
import AccountSettings from '@/pages/AccountSettings';
import BankrollSimulator from '@/pages/BankrollSimulator';
import BonusTermsDecoder from '@/pages/BonusTermsDecoder';
import PreDepositChecklist from '@/pages/PreDepositChecklist';
import WithdrawalPredictor from '@/pages/WithdrawalPredictor';
import GamblerPersonalityQuiz from '@/pages/GamblerPersonalityQuiz';
import WinPredictionSimulator from '@/pages/WinPredictionSimulator';
import FriendChallenge from '@/pages/FriendChallenge';
import SpinToWin from '@/pages/SpinToWin';
import SlotVolatilityMatcher from '@/pages/SlotVolatilityMatcher';
import BettingStreakTracker from '@/pages/BettingStreakTracker';
import MoodTracker from '@/pages/MoodTracker';
import LossChaseDetector from '@/pages/LossChaseDetector';
import WinCelebration from '@/pages/WinCelebration';
import SessionTimer from '@/pages/SessionTimer';
import AlternativeActivities from '@/pages/AlternativeActivities';
import RegretAnticipation from '@/pages/RegretAnticipation';
import ToolDiscovery from '@/pages/ToolDiscovery';
import ShareResult from '@/pages/ShareResult';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import ProtectedRoute from '@/components/ProtectedRoute';
import AuthCallback from '@/components/AuthCallback';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function ProtectedLayout({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-screen pt-16">{children}</main>
      <Footer />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasSessionId = location.hash?.includes('session_id=');

  useEffect(() => {
    if (hasSessionId) return;
    if (user) { setLoading(false); return; }
    const token = localStorage.getItem('casinoiq_token');
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [hasSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (hasSessionId) {
    return <AuthCallback onAuth={(u) => { setUser(u); setLoading(false); }} />;
  }

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('casinoiq_token');
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout }}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/dashboard" element={<ProtectedRoute><ProtectedLayout><Dashboard /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/play-smart/gambling-budget-calculator" element={<ProtectedRoute><ProtectedLayout><GamblingBudgetCalculator /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/play-smart/cost2play-calculator" element={<ProtectedRoute><ProtectedLayout><Cost2PlayCalculator /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/play-smart/rtp-comparison-engine" element={<ProtectedRoute><ProtectedLayout><RTPComparisonEngine /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/casino-partners" element={<ProtectedRoute><ProtectedLayout><CasinoPartners /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/account-settings" element={<ProtectedRoute><ProtectedLayout><AccountSettings /></ProtectedLayout></ProtectedRoute>} />
        {/* Play Smart Tools */}
        <Route path="/play-smart/bankroll-simulator" element={<ProtectedRoute><ProtectedLayout><BankrollSimulator /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/play-smart/bonus-terms-decoder" element={<ProtectedRoute><ProtectedLayout><BonusTermsDecoder /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/play-smart/pre-deposit-checklist" element={<ProtectedRoute><ProtectedLayout><PreDepositChecklist /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/play-smart/withdrawal-predictor" element={<ProtectedRoute><ProtectedLayout><WithdrawalPredictor /></ProtectedLayout></ProtectedRoute>} />
        {/* Viral Tools */}
        <Route path="/viral-tools/gambler-personality-quiz" element={<ProtectedLayout><GamblerPersonalityQuiz /></ProtectedLayout>} />
        <Route path="/viral-tools/win-prediction-simulator" element={<ProtectedRoute><ProtectedLayout><WinPredictionSimulator /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/viral-tools/friend-challenge" element={<ProtectedLayout><FriendChallenge /></ProtectedLayout>} />
        <Route path="/viral-tools/spin-to-win" element={<ProtectedRoute><ProtectedLayout><SpinToWin /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/viral-tools/slot-volatility-matcher" element={<ProtectedRoute><ProtectedLayout><SlotVolatilityMatcher /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/viral-tools/betting-streak-tracker" element={<ProtectedRoute><ProtectedLayout><BettingStreakTracker /></ProtectedLayout></ProtectedRoute>} />
        {/* Emotional Wellness */}
        <Route path="/emotional-wellness/mood-tracker" element={<ProtectedRoute><ProtectedLayout><MoodTracker /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/emotional-wellness/loss-chase-detector" element={<ProtectedRoute><ProtectedLayout><LossChaseDetector /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/emotional-wellness/win-celebration" element={<ProtectedRoute><ProtectedLayout><WinCelebration /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/emotional-wellness/session-timer" element={<ProtectedRoute><ProtectedLayout><SessionTimer /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/emotional-wellness/alternatives" element={<ProtectedRoute><ProtectedLayout><AlternativeActivities /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/emotional-wellness/regret-anticipation" element={<ProtectedRoute><ProtectedLayout><RegretAnticipation /></ProtectedLayout></ProtectedRoute>} />
        {/* Discovery & Share */}
        <Route path="/tools" element={<ProtectedRoute><ProtectedLayout><ToolDiscovery /></ProtectedLayout></ProtectedRoute>} />
        <Route path="/share/:type/:id" element={<ShareResult />} />
      </Routes>
      <Toaster theme="dark" richColors position="top-right" />
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
